import { useConvexMutation } from "@convex-dev/react-query";
import { useMutation } from "@tanstack/react-query";
import type { Id } from "@teachloop/backend/_generated/dataModel";
import { ConvexError } from "convex/values";
import type React from "react";
import { useCallback, useState } from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import Modal from "react-native-modal";
import Toast from "react-native-toast-message";
import { useFormatDate } from "@/elements/hooks/use-format-date";
import StudentMoodItem, {
  type Mood,
  moods,
} from "../lesson-history/student-mood-item";
import ResponsiveContent from "../shared/responsive-content";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { api } from "../utils/convex-api";

export interface PendingMoodReportProps {
  _id: Id<"lessonReports">;
  subject: string;
  tutorFirstName: string | null;
  lessonStart: number;
}

type ShareMoodModalProps = {
  pendingMoodReport: PendingMoodReportProps | null | undefined;
  isVisible: boolean;
  onComplete?: (lessonReportId: Id<"lessonReports">) => void;
  onDismiss?: () => void;
  mandatory?: boolean;
};

interface ShareMoodFormProps {
  lessonReportId: Id<"lessonReports">;
  subject: string;
  tutorFirstName: string | null;
  lessonStart: number;
  mandatory?: boolean;
  onComplete?: (lessonReportId: Id<"lessonReports">) => void;
  onDismiss?: () => void;
}

export function ShareMoodForm({
  lessonReportId,
  subject,
  tutorFirstName,
  lessonStart,
  mandatory = true,
  onComplete,
  onDismiss,
}: ShareMoodFormProps) {
  const formatDate = useFormatDate();

  const [mood, setMood] = useState<Mood["key"] | null>(null);
  const [studentComment, setStudentComment] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const { mutateAsync: setMoodAndComment, isPending } = useMutation({
    mutationFn: useConvexMutation(api.student.mood.mutations.setMoodAndComment),
  });

  const onShareMood = useCallback(async () => {
    try {
      if (lessonReportId && mood && studentComment.trim()) {
        const selectedMood = moods.find((item) => item.key === mood);

        await setMoodAndComment({
          lessonReportId,
          mood: selectedMood!.key,
          moodComment: selectedMood!.description,
          studentComment,
        });

        setError(null);
        setStudentComment("");
        setMood(null);
        setSubmitted(true);

        Toast.show({
          type: "info",
          text1: "😃 Thanks for sharing!",
        });

        onComplete?.(lessonReportId);
      }
    } catch (error) {
      setError(
        error instanceof ConvexError
          ? String(error.data)
          : "Failed to share mood",
      );
    }
  }, [lessonReportId, mood, studentComment, setMoodAndComment, onComplete]);

  return (
    <View>
      <View className="px-5 gap-8">
        <View>
          <Text className="font-geologica-medium text-teachLoop-accentPrimary text-xl leading-[1.5]">
            How was your lesson?
          </Text>

          <Text className="text-base font-geologica-regular leading-[1.5] text-teachLoop-textPrimary">
            We'd love to know how you're feeling after your{" "}
            <Text className="underline capitalize">{subject}</Text> lesson
            {tutorFirstName && (
              <Text>
                {" "}
                with{" "}
                <Text className="underline capitalize">{tutorFirstName}</Text>
              </Text>
            )}
            .
          </Text>

          <Text className="mt-1 text-sm font-geologica-medium leading-[1.5] text-teachLoop-textPrimary/70">
            {formatDate(lessonStart, "EEEE d MMMM")}
          </Text>

          {mandatory && (
            <Text className="mt-3 text-sm font-geologica-regular leading-[1.5] text-teachLoop-textPrimary/70">
              Complete this check-in before your next lesson can be booked.
            </Text>
          )}
        </View>

        <View className="gap-4">
          {moods.map((item) => (
            <Pressable key={item.key} onPress={() => setMood(item.key)}>
              <StudentMoodItem
                active={item.key === mood}
                studentMood={item.label}
                imageKey={item.emojiImageKey}
                studentMoodComment={item.description}
              />
            </Pressable>
          ))}
        </View>

        <View className="gap-4">
          <Text className="font-geologica-medium leading-6 font-medium text-[16px] text-teachLoop-textPrimary">
            Tell us more
          </Text>

          <Textarea
            placeholder={
              "Please share any additional information that you would like the tutor to know."
            }
            multiline
            size="lg"
            className="text-sm"
            value={studentComment}
            onChangeText={setStudentComment}
          />

          {error && (
            <Text className="text-sm font-geologica-medium text-red-500 text-center">
              {error}
            </Text>
          )}

          {submitted && !error && (
            <Text className="text-sm font-geologica-medium text-teachLoop-accentPrimary text-center">
              😃 Thanks for sharing!
            </Text>
          )}
        </View>

        <View className="items-center gap-2">
          <Button
            fullWidth
            size="sm"
            intent="primary"
            className="px-8"
            disabled={!mood || !studentComment.trim() || isPending || submitted}
            onPress={onShareMood}
          >
            <Button.Text size="sm">Share mood</Button.Text>
          </Button>

          {!mandatory && onDismiss && (
            <Pressable
              disabled={isPending}
              onPress={onDismiss}
              className="py-2 active:opacity-60"
            >
              <Text className="text-sm font-geologica-medium text-teachLoop-textPrimary/60 underline">
                Maybe later
              </Text>
            </Pressable>
          )}
        </View>
      </View>
    </View>
  );
}

function ShareMoodModal({
  pendingMoodReport,
  isVisible,
  onComplete,
  onDismiss,
  mandatory = true,
}: ShareMoodModalProps) {
  return (
    <Modal
      animationIn="slideInUp"
      animationOut="slideOutDown"
      isVisible={isVisible}
      onBackdropPress={mandatory ? undefined : onDismiss}
      onDismiss={mandatory ? undefined : onDismiss}
      useNativeDriver
      useNativeDriverForBackdrop
      hideModalContentWhileAnimating
    >
      <ResponsiveContent className="h-lvh bg-teachLoop-creamDark pt-safe">
        <ScrollView className="flex-1 py-4">
          {pendingMoodReport && (
            <ShareMoodForm
              lessonReportId={pendingMoodReport._id}
              subject={pendingMoodReport.subject}
              tutorFirstName={pendingMoodReport.tutorFirstName}
              lessonStart={pendingMoodReport.lessonStart}
              mandatory={mandatory}
              onComplete={onComplete}
            />
          )}
        </ScrollView>
      </ResponsiveContent>
    </Modal>
  );
}

export default ShareMoodModal;
